<script lang="ts">
  import { fly } from 'svelte/transition';
  import { me } from '$src/stores/user.store';
  import { deleteTagRequest } from '$src/utils/api/tagging.util';
  import { progressManager } from '$src/stores/posts.store';
  import { createEventDispatcher, onMount } from 'svelte';
  import type { FPost } from 'garden-common';

  import Close from '$icons/CloseAltIcon.svelte';
  import TooltipStatic from '../tooltips/TooltipStatic.svelte';
  import { decodeAverageColor } from 'garden-utils/index';
  import { updateBlur } from 'garden-utils/index';
  import type { RGBColor } from 'garden-utils/index';
  import { isMobile } from '$src/stores/media.store';
  import type { Writable } from 'svelte/store';
  import Media from '../media/Media.svelte';

  export let mediaIndex: number = 0;
  export let groupIndex: number = 0;

  export let mode: 'normal' | 'grouping' | 'tagging' | 'preview' = 'normal';
  export let owner: FPost<string>['creator'];
  export let uploadable: boolean = true;
  // TODO: TYPING: These can have a better type
  export let tags = [];
  export let media: any;

  const dispatch = createEventDispatcher();

  // Blurhash related
  export let imageDataUrl: string | undefined = '';
  let avgColor: RGBColor | null = null;

  // Tagging related
  let hoveredTag: string = null;
  let taggingOverlayDiv: HTMLButtonElement;
  let taggingOverlayDivHeight: number;
  let taggingOverlayDivWidth: number;
  let copied: boolean = false;

  $: blurOverlayDivHeight = taggingOverlayDivHeight;
  $: blurOverlayDivWidth = taggingOverlayDivWidth;
  let tagInfoCardOpen = true;

  let uploadProgress: Writable<number>;

  $: if (uploadable && media && media[mediaIndex] && media[mediaIndex].images[groupIndex]) {
    progressManager
      .getProgressStore(media[mediaIndex].images[groupIndex], mediaIndex)
      .then(store => {
        uploadProgress = store;
      });
  }

  let blurredBackdrop = true;
  let intervalId = setInterval(() => {
    blurredBackdrop = !blurredBackdrop;
  }, 2000);

  // Stop the oscillation after a certain time
  setTimeout(() => {
    clearInterval(intervalId);
  }, 80000);

  $: if (mode === 'tagging') {
    tagInfoCardOpen = true;
  }
  let lastMediaIndex: number | null = null;
  let lastGroupIndex: number | null = null;
  let lastBlurhash: string | null = null;
  const reRenderBlurAndDomColor = (mediaIndex: Readonly<number>, groupIndex: Readonly<number>) => {
    if (media && media[mediaIndex] && media[mediaIndex].images[groupIndex]) {
      try {
        if (!media[mediaIndex].blurhash[groupIndex]) {
          throw new Error(`No blurhash found at ${groupIndex}`);
        }

        if (
          mediaIndex !== lastMediaIndex ||
          groupIndex !== lastGroupIndex ||
          lastBlurhash !== media[mediaIndex].blurhash[groupIndex]
        ) {
          lastMediaIndex = mediaIndex ?? 0;
          lastGroupIndex = groupIndex ?? 0;

          // if the last blurhash is falsy and the blurhash is truthy , set it to last
          if (!lastBlurhash) {
            lastBlurhash = media[mediaIndex].blurhash[groupIndex];
          }

          if (taggingOverlayDiv && taggingOverlayDivWidth && taggingOverlayDivHeight) {
            imageDataUrl = updateBlur(media[mediaIndex].blurhash[groupIndex], {
              width: taggingOverlayDivWidth,
              height: taggingOverlayDivHeight
            });
          } else {
            setTimeout(() => {
              reRenderBlurAndDomColor(mediaIndex, groupIndex);
            }, 1000);
          }
        }

        imageDataUrl = imageDataUrl;

        avgColor = decodeAverageColor(media[mediaIndex].blurhash[groupIndex]);
      } catch (error) {
        // console.log('Blurhash not yet populated ? ', error.message);
        setTimeout(() => {
          reRenderBlurAndDomColor(mediaIndex, groupIndex);
        }, 1000);
      }
    }
  };

  $: if (taggingOverlayDiv) {
    reRenderBlurAndDomColor(mediaIndex, groupIndex);
  }

  onMount(async () => {
    reRenderBlurAndDomColor(mediaIndex, groupIndex);
  });
</script>

<button
  class="tagging-overlay h-full w-full relative top-0 left-0 bg-black z-[0]"
  class:cursor-pointer={mode === 'tagging'}
  bind:this={taggingOverlayDiv}
  bind:clientHeight={taggingOverlayDivHeight}
  bind:clientWidth={taggingOverlayDivWidth}
  on:keydown
  on:click={e => {
    if (mode !== 'tagging') return;
    const rect = e.currentTarget.getBoundingClientRect();
    dispatch('tag', {
      x: (e.clientX - rect.left) / taggingOverlayDivWidth,
      y: (e.clientY - rect.top) / taggingOverlayDivHeight
    });
  }}
>
  {#if imageDataUrl}
    {#key imageDataUrl}
      <div class="absolute z-[5]">
        <Media src={imageDataUrl} />
      </div>
    {/key}
  {/if}

  <div class="w-full relative h-full z-[10] ">
    {#if tags && typeof tags[0] !== 'string' && tags[0]?.tagged}
      {#each tags.filter(t => typeof t !== 'string' && t.tagged.mediaIndex === mediaIndex && t.tagged.groupIndex === groupIndex) as tag, i}
        {#if typeof tag !== 'string'}
          <TooltipStatic
            location={{
              x:
                tag.tagged.pos.x *
                (taggingOverlayDivWidth ? taggingOverlayDivWidth : taggingOverlayDiv?.clientWidth),
              y:
                tag.tagged.pos.y *
                (taggingOverlayDivHeight
                  ? taggingOverlayDivHeight
                  : taggingOverlayDiv?.clientHeight)
            }}
            arrow={tag.tagged.pos.y > taggingOverlayDiv?.getBoundingClientRect().height / 2
              ? 'top'
              : 'bottom'}
            on:mouseover={() => {
              if (typeof tag === 'string' || owner !== $me._id) return;
              hoveredTag = tag._id;
            }}
            on:mouseleave={() => {
              if (owner !== $me._id) return;
              hoveredTag = null;
            }}
          >
            <div class="flex items-center gap-1">
              {#if tag.tagged.profileImage}
                <img
                  src={tag.tagged.profileImage}
                  class="w-3 h-3 cursor-pointer rounded-full"
                  alt=""
                />
              {:else}
                <img
                  src={`https://api.dicebear.com/6.x/thumbs/svg?backgroundColor=B5F1D4&shapeColor=074D2B&seed=${tag.tagged.username}`}
                  class="w-3 h-3 rounded-full cursor-pointer"
                  alt=""
                />
              {/if}

              <div class="font-medium text-xs mb-1">{tag.tagged.username}</div>
              {#if hoveredTag === tag._id}
                <button
                  transition:fly|global={{ x: -9, duration: 200 }}
                  class="cursor-pointer"
                  on:click|stopPropagation={() => {
                    if (typeof tag === 'string') return;

                    deleteTagRequest(tag._id)
                      .then(() => {
                        tags.splice(i);
                        tags = tags;
                      })
                      .catch(console.log);
                  }}
                >
                  <Close size={14} color="white" hoveredColor="red" />
                </button>
              {/if}
            </div>
          </TooltipStatic>
        {/if}
      {/each}
    {/if}

    <div class="w-full h-full relative z-0">
      {#if taggingOverlayDiv}
        {#if uploadProgress}
          {#key media}
            {#if $uploadProgress !== 0 && $uploadProgress !== 100}
              {#if $uploadProgress !== -1}
                <div
                  bind:clientHeight={blurOverlayDivHeight}
                  bind:clientWidth={blurOverlayDivWidth}
                  class:backdrop-blur-sm={blurredBackdrop}
                  class="pointer-events-none transition-all duration-700 bg-stone-600/40 backdrop-blur-sm absolute w-full h-full"
                  style="width: 100%; height: {100 - $uploadProgress}%;"
                >
                  <small
                    class="px-4 py-2 rounded-md absolute left-2 top-2 text-stone-700 font-dm-sans bg-white/90 font-bold"
                    >Uploaded {Math.floor($uploadProgress)}%</small
                  >
                </div>
              {:else}
                <div
                  bind:clientHeight={blurOverlayDivHeight}
                  bind:clientWidth={blurOverlayDivWidth}
                  class:backdrop-blur-sm={blurredBackdrop}
                  class="pointer-events-none transition-all duration-700 bg-amber-600/40 backdrop-blur-sm absolute w-full h-full"
                  style="width: 100%; height: 100%;"
                >
                  <small
                    class="px-4 py-2 rounded-md absolute left-2 top-2 text-stone-700 font-dm-sans bg-white/90 font-bold"
                    >File size too large ! Maximum size allowed is 10 MB.</small
                  >
                </div>
              {/if}
            {/if}
            <!-- {#if avgColor}
              <button
                on:click={() => {
                  copied = true;
                  navigator.clipboard.writeText(
                    `rgb(${avgColor.r}, ${avgColor.g}, ${avgColor.b}, 1)`
                  );
                  setTimeout(() => {
                    copied = false;
                  }, 2000);
                }}
                class="px-4 py-2 cursor-pointer rounded-md absolute {$isMobile
                  ? 'left-2'
                  : 'right-2'}  top-2 text-stone-700 font-dm-sans bg-white/90 font-bold flex flex-row justify-center items-center gap-2"
              >
                <div
                  class="w-6 h-6 rounded-sm"
                  style="background-color : rgb({avgColor.r}, {avgColor.g}, {avgColor.b}, 1)"
                ></div>
                <small>
                  Dominant color :
                  {#if copied}
                    <span class="text-green-600">copied!</span>
                  {:else}
                    rgb({avgColor.r}, {avgColor.g}, {avgColor.b})
                  {/if}
                </small>
              </button>
            {/if} -->
          {/key}
        {/if}
      {/if}
      <slot />
    </div>
  </div>
</button>
